import React, { useEffect, useState } from 'react';
import { Card, Button, Select, message, Space, Modal, Input, Upload, Typography, Tooltip, Tag } from 'antd';
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
  ArrowLeftOutlined,
  FileTextOutlined,
  PictureOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { apiClient } from 'Utils/api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { v4 as uuidv4 } from 'uuid';
import { useLanguage } from '../../Context/LanguageContext';

const { Title, Text } = Typography;

const LAYOUTS = [
  { value: '1col', label: '1 Spalte' },
  { value: '2col', label: '2 Spalten (1:1)' },
  { value: '3col', label: '3 Spalten (1:1:1)' },
  { value: '4col', label: '4 Spalten (1:1:1:1)' },
  { value: '1-2', label: '2 Spalten (1/3 : 2/3)' },
  { value: '2-1', label: '2 Spalten (2/3 : 1/3)' },
  { value: '1-3', label: '2 Spalten (1/4 : 3/4)' },
  { value: '3-1', label: '2 Spalten (3/4 : 1/4)' },
  { value: '1-2-1', label: '3 Spalten (1/4 : 2/4 : 1/4)' },
  { value: '1-1-2', label: '3 Spalten (1/4 : 1/4 : 2/4)' },
  { value: '2-1-1', label: '3 Spalten (2/4 : 1/4 : 1/4)' },
];

const LAYOUT_COL_COUNT: Record<string, number> = {
  '1col': 1, '2col': 2, '3col': 3, '4col': 4,
  '1-2': 2, '2-1': 2, '1-3': 2, '3-1': 2,
  '1-2-1': 3, '1-1-2': 3, '2-1-1': 3,
};

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, 4, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};
const QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'list', 'bullet', 'link'];

interface ImageMeta {
  id: string;
  originalUrl: string;
  thumbnailUrl: string | null;
  title: Record<string, string>;
  altText: Record<string, string>;
  copyright: Record<string, string>;
}

interface Column {
  id: string;
  type: 'text' | 'image';
  content: any;
}

interface Row {
  id: string;
  layout: string;
  columns: Column[];
}

const emptyText = (): Column => ({
  id: uuidv4(),
  type: 'text',
  content: { value: { de: '', en: '' } },
});

const PageEditorPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const activeLang = currentLanguage as 'de' | 'en';
  const isDefault = activeLang === 'de';
  const [pageLabel, setPageLabel] = useState('');
  const [rows, setRows] = useState<Row[]>([]);
  const [imagesMap, setImagesMap] = useState<Record<string, ImageMeta>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imageMetaEditor, setImageMetaEditor] = useState<ImageMeta | null>(null);

  useEffect(() => {
    if (!slug) return;
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchPage = async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`/api/admin/pages/${slug}`);
      const page = res.data.page;
      setPageLabel(page.label);
      // ensure each row/col has an id
      const enriched = (page.rows || []).map((r: any) => ({
        id: r.id || uuidv4(),
        layout: r.layout || '1col',
        columns: (r.columns || []).map((c: any) => ({
          id: c.id || uuidv4(),
          type: c.type || 'text',
          content: c.content || (c.type === 'image' ? { imageIds: [] } : { value: { de: '', en: '' } }),
        })),
      }));
      setRows(enriched);
      setImagesMap(res.data.images || {});
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Fehler beim Laden der Seite');
    } finally {
      setLoading(false);
    }
  };

  const updateColumn = (rowId: string, colId: string, patch: Partial<Column>) =>
    setRows((prev) =>
      prev.map((r) =>
        r.id === rowId
          ? { ...r, columns: r.columns.map((c) => (c.id === colId ? { ...c, ...patch } : c)) }
          : r
      )
    );

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        id: uuidv4(),
        layout: '1col',
        columns: [emptyText()],
      },
    ]);
  };

  const removeRow = (rowId: string) => {
    Modal.confirm({
      title: 'Row löschen?',
      content: 'Diese Aktion kann nicht rückgängig gemacht werden.',
      okText: 'Löschen',
      okButtonProps: { danger: true },
      onOk: () => setRows((prev) => prev.filter((r) => r.id !== rowId)),
    });
  };

  const moveRow = (rowId: string, direction: -1 | 1) => {
    setRows((prev) => {
      const idx = prev.findIndex((r) => r.id === rowId);
      const newIdx = idx + direction;
      if (idx < 0 || newIdx < 0 || newIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      return next;
    });
  };

  const changeLayout = (rowId: string, layout: string) => {
    const targetCount = LAYOUT_COL_COUNT[layout] || 1;
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== rowId) return r;
        let cols = [...r.columns];
        while (cols.length < targetCount) cols.push(emptyText());
        if (cols.length > targetCount) cols = cols.slice(0, targetCount);
        return { ...r, layout, columns: cols };
      })
    );
  };

  const changeColumnType = (rowId: string, colId: string, type: 'text' | 'image') => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== rowId) return r;
        return {
          ...r,
          columns: r.columns.map((c) =>
            c.id === colId
              ? { ...c, type, content: type === 'image' ? { imageIds: [] } : { value: { de: '', en: '' } } }
              : c
          ),
        };
      })
    );
  };

  const handleSave = async () => {
    if (!slug) return;
    setSaving(true);
    try {
      // Strip resolved images before saving (only persist imageIds)
      const cleanRows = rows.map((r) => ({
        ...r,
        columns: r.columns.map((c) => {
          if (c.type === 'image') {
            return { ...c, content: { imageIds: c.content?.imageIds || [] } };
          }
          return c;
        }),
      }));
      await apiClient.put(`/api/admin/pages/${slug}`, { rows: cleanRows });
      message.success('Seite gespeichert');
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (rowId: string, colId: string, files: File[]) => {
    const fd = new FormData();
    files.forEach((f) => fd.append('images', f));
    fd.append('data', JSON.stringify({ pageSlug: slug }));
    try {
      const res = await apiClient.post('/api/admin/page-images', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newImages: ImageMeta[] = res.data.images || [];
      const newMap = { ...imagesMap };
      newImages.forEach((img) => {
        newMap[img.id] = img;
      });
      setImagesMap(newMap);
      const col = rows.find((r) => r.id === rowId)?.columns.find((c) => c.id === colId);
      const existing: string[] = col?.content?.imageIds || [];
      updateColumn(rowId, colId, {
        content: { imageIds: [...existing, ...newImages.map((i) => i.id)] },
      });
      message.success(`${newImages.length} Bild(er) hochgeladen`);
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Upload fehlgeschlagen');
    }
  };

  const removeImageFromColumn = (rowId: string, colId: string, imageId: string) => {
    const col = rows.find((r) => r.id === rowId)?.columns.find((c) => c.id === colId);
    if (!col) return;
    const filtered = (col.content?.imageIds || []).filter((id: string) => id !== imageId);
    updateColumn(rowId, colId, { content: { imageIds: filtered } });
  };

  const saveImageMeta = async (image: ImageMeta) => {
    try {
      await apiClient.put(`/api/admin/page-images/${image.id}`, {
        title: image.title,
        altText: image.altText,
        copyright: image.copyright,
      });
      setImagesMap((prev) => ({ ...prev, [image.id]: image }));
      message.success('Metadaten gespeichert');
      setImageMetaEditor(null);
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Fehler');
    }
  };

  if (loading) return <div>Lade Seite…</div>;

  return (
    <div>
      <Space style={{ marginBottom: 16, justifyContent: 'space-between', width: '100%' }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/pages')}>
            Zurück
          </Button>
          <Title level={3} style={{ margin: 0 }}>
            {pageLabel}
          </Title>
        </Space>
        <Tag color={isDefault ? 'blue' : 'gold'} data-testid="active-language-tag">
          Sprache: {activeLang.toUpperCase()}{isDefault ? ' (Standard)' : ''}
        </Tag>
      </Space>

      <Card style={{ marginBottom: 16 }}>
        <div style={{ textAlign: 'right' }}>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={handleSave}
            data-testid="save-page-btn"
          >
            Speichern
          </Button>
        </div>
      </Card>

      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {rows.map((row, rowIdx) => (
          <Card
            key={row.id}
            title={`Row ${rowIdx + 1}`}
            data-testid={`row-${rowIdx}`}
            extra={
              <Space>
                <Tooltip title="Hoch">
                  <Button
                    icon={<ArrowUpOutlined />}
                    onClick={() => moveRow(row.id, -1)}
                    disabled={rowIdx === 0}
                  />
                </Tooltip>
                <Tooltip title="Runter">
                  <Button
                    icon={<ArrowDownOutlined />}
                    onClick={() => moveRow(row.id, 1)}
                    disabled={rowIdx === rows.length - 1}
                  />
                </Tooltip>
                <Tooltip title="Löschen">
                  <Button danger icon={<DeleteOutlined />} onClick={() => removeRow(row.id)} />
                </Tooltip>
              </Space>
            }
          >
            <Space style={{ marginBottom: 12 }}>
              <Text>Layout:</Text>
              <Select
                style={{ width: 280 }}
                value={row.layout}
                onChange={(v) => changeLayout(row.id, v)}
                options={LAYOUTS}
              />
            </Space>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${row.columns.length}, 1fr)`, gap: 12 }}>
              {row.columns.map((col, colIdx) => (
                <Card key={col.id} size="small" title={`Col ${colIdx + 1}`}>
                  <Space style={{ marginBottom: 12 }}>
                    <Button
                      type={col.type === 'text' ? 'primary' : 'default'}
                      icon={<FileTextOutlined />}
                      size="small"
                      onClick={() => changeColumnType(row.id, col.id, 'text')}
                    >
                      Text
                    </Button>
                    <Button
                      type={col.type === 'image' ? 'primary' : 'default'}
                      icon={<PictureOutlined />}
                      size="small"
                      onClick={() => changeColumnType(row.id, col.id, 'image')}
                    >
                      Bild
                    </Button>
                  </Space>
                  {col.type === 'text' && (
                    <ReactQuill
                      theme="snow"
                      modules={QUILL_MODULES}
                      formats={QUILL_FORMATS}
                      value={col.content?.value?.[activeLang] || ''}
                      onChange={(html) =>
                        updateColumn(row.id, col.id, {
                          content: {
                            value: {
                              ...(col.content?.value || {}),
                              [activeLang]: html,
                            },
                          },
                        })
                      }
                    />
                  )}
                  {col.type === 'image' && (
                    <div>
                      <Upload
                        multiple
                        accept="image/*"
                        beforeUpload={(file, fileList) => {
                          // upload all selected at once when last reached
                          const isLast = fileList[fileList.length - 1].uid === file.uid;
                          if (isLast) {
                            handleImageUpload(row.id, col.id, fileList as File[]);
                          }
                          return false;
                        }}
                        showUploadList={false}
                      >
                        <Button icon={<UploadOutlined />} size="small">
                          Bilder hochladen
                        </Button>
                      </Upload>
                      <div
                        style={{
                          marginTop: 12,
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
                          gap: 8,
                        }}
                      >
                        {(col.content?.imageIds || []).map((imgId: string) => {
                          const img = imagesMap[imgId];
                          if (!img) return null;
                          const apiBase = process.env.REACT_APP_API_URL || '';
                          return (
                            <div
                              key={imgId}
                              style={{ position: 'relative', border: '1px solid #eee', borderRadius: 4, overflow: 'hidden' }}
                            >
                              <img
                                src={`${apiBase}${img.thumbnailUrl || img.originalUrl}`}
                                alt=""
                                style={{ width: '100%', height: 80, objectFit: 'cover', cursor: 'pointer' }}
                                onClick={() => setImageMetaEditor(img)}
                              />
                              <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                style={{ position: 'absolute', top: 2, right: 2 }}
                                onClick={() => removeImageFromColumn(row.id, col.id, imgId)}
                              />
                            </div>
                          );
                        })}
                      </div>
                      {(col.content?.imageIds || []).length > 1 && (
                        <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                          Mehrere Bilder werden im Frontend automatisch als Slider angezeigt.
                        </Text>
                      )}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </Card>
        ))}
      </Space>

      <div style={{ marginTop: 16, textAlign: 'center' }}>
        <Button type="dashed" icon={<PlusOutlined />} onClick={addRow} data-testid="add-row-btn">
          Row hinzufügen
        </Button>
      </div>

      <Modal
        title={`Bild-Metadaten (${activeLang.toUpperCase()})`}
        open={!!imageMetaEditor}
        onCancel={() => setImageMetaEditor(null)}
        onOk={() => imageMetaEditor && saveImageMeta(imageMetaEditor)}
        width={520}
      >
        {imageMetaEditor && (
          <div>
            <img
              src={`${process.env.REACT_APP_API_URL || ''}${imageMetaEditor.thumbnailUrl || imageMetaEditor.originalUrl}`}
              alt=""
              style={{ maxWidth: '100%', marginBottom: 16, borderRadius: 4 }}
            />
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text>Titel</Text>
                <Input
                  value={imageMetaEditor.title?.[activeLang] || ''}
                  placeholder={!isDefault ? imageMetaEditor.title?.de || '' : ''}
                  onChange={(e) =>
                    setImageMetaEditor({
                      ...imageMetaEditor,
                      title: { ...(imageMetaEditor.title || {}), [activeLang]: e.target.value },
                    })
                  }
                />
              </div>
              <div>
                <Text>Alt-Text</Text>
                <Input
                  value={imageMetaEditor.altText?.[activeLang] || ''}
                  placeholder={!isDefault ? imageMetaEditor.altText?.de || '' : ''}
                  onChange={(e) =>
                    setImageMetaEditor({
                      ...imageMetaEditor,
                      altText: { ...(imageMetaEditor.altText || {}), [activeLang]: e.target.value },
                    })
                  }
                />
              </div>
              <div>
                <Text>Copyright</Text>
                <Input
                  value={imageMetaEditor.copyright?.[activeLang] || ''}
                  placeholder={!isDefault ? imageMetaEditor.copyright?.de || '' : ''}
                  onChange={(e) =>
                    setImageMetaEditor({
                      ...imageMetaEditor,
                      copyright: { ...(imageMetaEditor.copyright || {}), [activeLang]: e.target.value },
                    })
                  }
                />
              </div>
              {!isDefault && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Sprache via globalen Schalter oben rechts wechseln. DE-Werte werden als Placeholder angezeigt.
                </Text>
              )}
            </Space>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PageEditorPage;
